import { ApplicationConfig, ErrorHandler, importProvidersFrom, InjectionToken, NgZone } from '@angular/core'
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http'
import { provideRouter, RouteReuseStrategy } from '@angular/router'
import { OverlayModule } from '@angular/cdk/overlay'
import { registerLocaleData } from '@angular/common'

// Translation
import localeEs from '@angular/common/locales/es'
import localeEn from '@angular/common/locales/en'
import localeNl from '@angular/common/locales/nl'
import localeFr from '@angular/common/locales/fr'
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core'
import { I18nMergedTranslateLoader } from '@core/i18n/i18n.translate-loader'

// Ionic & Capacitor Plugins
import { IonicRouteStrategy, ModalController, PopoverController, Platform } from '@ionic/angular'
import { provideIonicAngular } from '@ionic/angular/standalone'
import { Smartlook } from '@awesome-cordova-plugins/smartlook/ngx'
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx'
import { NetworkInterface } from '@awesome-cordova-plugins/network-interface/ngx'
import { Browser } from 'ionic-appauth'
import { CapacitorBrowser, CapacitorSecureStorage } from 'ionic-appauth/lib/capacitor'

// NgRx
import { ActionReducerMap, provideStore, Store } from '@ngrx/store'
import { rootEffects, rootMetaReducers, rootReducers } from '@core/store/store.provider'
import { provideEffects } from '@ngrx/effects'
import { ngRxDevToolsConfig } from './ngrx-devtools-config'
import { provideRouterStore, routerReducer } from '@ngrx/router-store'
import { NgrxRouterEffects } from '@core/ngrx-router/state/effects/ngrx-router.effects'
import { MergedRouterStateSerializer } from '@core/ngrx-router/serializers/merged-route.serializer'

// Core & Authentication
import { Requestor, StorageBackend } from '@openid/appauth'
import { CoreAuthService } from '@core/auth/services/core-auth-service'
import { authFactory } from '@core/auth/factories/auth.factory'
import { httpFactory } from '@core/requests/factories/http.factory'
import { httpInterceptorProviders } from '@core/requests/interceptors'
import { MCMErrorHandler } from '@core/errors/error-handler'
import { routes } from './app.routes'

// Providers
import { provideAggregations } from '@features/aggregations/aggregations.provider'
import { provideMetricDetail } from '@features/metric-detail/metric-detail.provider'
import { provideRankings } from '@features/rankings/rankings.provider'
import { provideSeasons } from '@features/seasons/seasons.providers'
import { provideSelections } from '@features/selections/selections.provider'
import { provideUpdater } from '@core/services/updater/updater.provider'
import { provideWebSockets } from '@sockets/sockets.provider'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'

registerLocaleData(localeEs)
registerLocaleData(localeEn)
registerLocaleData(localeNl)
registerLocaleData(localeFr)

export const REDUCER_TOKEN = new InjectionToken<ActionReducerMap<any>>('mobile')
export function getReducers() {
  return {
    ...rootReducers,
    router: routerReducer
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    //TODO: Remove @angular/animations from package.json when migrating to Angular 22. Temporarily kept for compatibility with @mediacoach/ui.
    provideAnimationsAsync(),
    provideIonicAngular({ mode: 'md' }),
    provideStore(REDUCER_TOKEN, { metaReducers: rootMetaReducers }),
    provideEffects([...rootEffects, NgrxRouterEffects]),
    provideRouterStore({
      stateKey: 'router',
      serializer: MergedRouterStateSerializer
    }),
    provideTranslateService({
      lang: 'es',
      fallbackLang: 'es',
      loader: {
        provide: TranslateLoader,
        useClass: I18nMergedTranslateLoader
      }
    }),
    { provide: REDUCER_TOKEN, useFactory: getReducers },
    importProvidersFrom(ngRxDevToolsConfig, OverlayModule),
    provideAggregations(),
    provideMetricDetail(),
    provideRankings(),
    provideSeasons(),
    provideSelections(),
    provideUpdater(),
    provideWebSockets(),
    ModalController,
    PopoverController,
    Smartlook,
    ScreenOrientation,
    NetworkInterface,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: MCMErrorHandler },
    httpInterceptorProviders,
    { provide: StorageBackend, useClass: CapacitorSecureStorage },
    { provide: Requestor, useFactory: httpFactory, deps: [Platform, HttpClient] },
    { provide: Browser, useClass: CapacitorBrowser },
    {
      provide: CoreAuthService,
      useFactory: authFactory,
      deps: [Platform, NgZone, Browser, StorageBackend, Requestor, Store]
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes)
  ]
}
